
import { useState, useContext, useRef, useEffect } from 'react';
import { Button, Form, Container, Row, Col, Image, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from 'axios';
import Voices from '../voices.json'
import Avatars from '../avatars.json'
import { AppContext } from '../App';
import { Spinner } from 'react-bootstrap';


function Configure({ goToDemo }) {

    axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;

    const { configData, setConfigData, configList, setConfigList, getAllConfigurations, configName, setConfigName, getConfiguration, loading, setLoading } = useContext(AppContext);
    const [localConfig, setLocalConfig] = useState({});

    useEffect(() => {
        if (configData) {
            setLocalConfig({ ...configData });
        }
    }, [configData?.id]); 
    
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({})
    const [message, setMessage] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]); // to keep track of selected files
    const fileRef = useRef(null);
    const [isChangeSave, setIsChangeSave] = useState(true);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
        setMessage(null)
        const form = e.currentTarget;
        const agentName = form.agentName.value.trim();
        const instructions = form.instructions.value.trim();
        const firstMessage = form.firstMessage.value.trim();
        const avatar = form.avatar.value;
        const voice = form.voice.value;
        const files = Array.from(form.files.files);
        setSelectedFiles(files);
        setFormData({ agentName: agentName, instructions: instructions, firstMessage: firstMessage, avatar: avatar, voice: voice });
    }

    // Helper for Saving/Updating (Expects IDs for objects)
    const createSaveFormData = (config, extraFiles = []) => {
        const data = new FormData();
        Object.keys(config).forEach((key) => {
            const value = config[key];
            if (typeof value === "object" && value !== null) {
                // specific logic from handleConfigSave/updateConfig
                if (value.id) {
                    data.append(key, value.id);
                } else {
                    data.append(key, JSON.stringify(value));
                }
            } else {
                data.append(key, value);
            }
        });
        extraFiles.forEach((file) => {
            data.append("files", file);
        });
        return data;
    }

    const createAgentFormData = (config) => {
        const data = new FormData();
        Object.keys(config).forEach((key) => {
            const value = config[key];
            if (typeof value === "object" && value !== null) {
                data.append(key, JSON.stringify(value));
            } else {
                data.append(key, value);
            }
        });
        return data;
    }

    const handleConfigSave = async () => {
        const isConfigNameExists = configList.some((config) => config.name.trim() === configName.trim());
        if (isConfigNameExists) {
            setMessage("Name already exists.")
        } else {
            setMessage(null)
            setLoading('save-config');
            let newConfig = { name: configName, ...formData }
            setFormData(newConfig)

            // Usign Helper
            const formDataToSubmit = createSaveFormData(newConfig, selectedFiles);
            formDataToSubmit.append("name", configName); // Ensure name is definitely there

            const result = await axios.post('/save_configuration', formDataToSubmit, { headers: { 'Content-Type': 'multipart/form-data' } })
            const newConfigId = result.data.new_config_id
            newConfig = await getConfiguration(newConfigId)
            setLoading('')
            setShowModal(false)
            setSelectedFiles([]);
            fileRef.current.value = '';
            const configurationList = await getAllConfigurations()
            setConfigList(configurationList)
            // Update global config data only after save
            setConfigData(newConfig);
            alert("Configuration saved successfully. If you uploaded documents, indexing may take a few moments.")
        }
    }
    const handleSelectChange = (e) => {
        setIsChangeSave(false)
        const { name, value } = e.target;
        if (name === "voice") {
            const data = Voices.find((voice) => voice.id === parseInt(value))
            setLocalConfig({ ...localConfig, [name]: data });
        }

        if (name === "avatar") {
            const data = Avatars.find((avatar) => avatar.id === parseInt(value))
            setLocalConfig({ ...localConfig, [name]: data });
        }
    }

    const handleChange = (e) => {
        setIsChangeSave(false)
        const { name, value } = e.target;
        setLocalConfig({ ...localConfig, [name]: value });
    }

    const handleFileChange = (e) => {
        setIsChangeSave(false)
        const filesArray = Array.from(e.target.files);
        setSelectedFiles(filesArray)
    }

    const deleteUploadedFile = (index) => {
        const deletedFile = localConfig.uploaded_files[index];
        try {
            axios.delete("/delete_uploaded_file", { data: { config_name: localConfig.name, file_name: deletedFile }, headers: { "Content-Type": "application/json" } });
            alert(`Deleted file: ${deletedFile} for configuration: ${localConfig.name}`);
            const newFileList = localConfig?.uploaded_files?.filter((file, i) => file !== deletedFile);
            setLocalConfig({ ...localConfig, uploaded_files: newFileList });
            setConfigData({ ...configData, uploaded_files: newFileList });
        }
        catch (error) {
            console.error("Error deleting uploaded file:", error);
        }
    }

    const updateConfig = async () => {
        const formDataToUpdate = createSaveFormData(localConfig, selectedFiles);

        try {
            setLoading('update-config');
            const response = await axios.put(`/update_config/${localConfig.id}`, formDataToUpdate,
                { headers: { "Content-Type": 'multipart/form-data' } })
            const updatedConfiguration = await getConfiguration(localConfig.id)

            setConfigData(updatedConfiguration)
            setLocalConfig(updatedConfiguration);

            setSelectedFiles([]);
            fileRef.current.value = '';
            setLoading('')
            setIsChangeSave(true);
            alert("Update Configuration Successfully. If you uploaded documents, indexing may take a few moments.")
        }
        catch (error) {
            console.log("error: ", error);
        }
    }

    //Deploy config for production cloud agent
    const deployAgent = async () => {
        try {
            setLoading('deploy-agent');
            const latestConfig = await getConfiguration(configData.id);
            const data = createAgentFormData(latestConfig);

            await axios.post('/deploy_agent', data, { headers: { 'Content-Type': 'multipart/form-data' } })
            setLoading('')
            alert("Deployed successfully!")
        }
        catch (e) {
            console.error('Failed to deploy agent', e)
            throw e
        }
    }

    return (<>
        <Container fluid className='p-2 pt-md-3 pb-md-3 px-lg-5'>
            <Form onSubmit={handleSubmit} enctype="multipart/form-data">
                <Row className="g-5 mx-auto justify-content-center">
                    <Col lg={6} className='p-1 px-md-3'>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-start w-100">Agent Name</Form.Label>
                            <Form.Control name="agentName" type="text" placeholder="Enter agent name" value={localConfig.agentName || ''} onChange={handleChange} required />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="text-start w-100">Instructions</Form.Label>
                            <Form.Control as="textarea" name="instructions" placeholder="Enter instructions" value={localConfig.instructions || ''} onChange={handleChange} style={{ height: "150px", overflowY: "auto", resize: "vertical" }} required />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="text-start w-100">
                                Upload Knowledge Base
                            </Form.Label>

                            <Form.Control
                                type="file"
                                name="files"
                                ref={fileRef}
                                multiple
                                onChange={handleFileChange}
                                placeholder="Upload knowledge base files"
                            />

                            {/* Selected Files Section */}
                            {selectedFiles && selectedFiles.length > 0 && (
                                <div className="mt-3">
                                    <Form.Label className="text-start w-100">
                                        Selected Files
                                    </Form.Label>

                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '10px',
                                            marginTop: '12px',
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            paddingBottom: '8px',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {selectedFiles.map((file, index) => (
                                            <div
                                                key={`selected-${index}`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '6px 12px',
                                                    backgroundColor: '#b1a0de',
                                                    borderRadius: '6px',
                                                    border: '1px solid #ddd',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: '13px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '200px',
                                                    }}
                                                >
                                                    {file.name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}


                            {/* Uploaded Files Section - use localConfig */}
                            {localConfig?.uploaded_files && localConfig?.uploaded_files?.length > 0 &&
                                (<div className="mt-3">
                                    <Form.Label className="text-start w-100">Uploaded Files</Form.Label>
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '12px', overflowX: 'auto', overflowY: 'hidden', paddingBottom: '8px', maxWidth: '100%' }}>
                                        {localConfig.uploaded_files.map((file, index) => (
                                            <div
                                                key={`uploaded-${index}`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '6px 12px',
                                                    backgroundColor: '#bfbfbf',
                                                    borderRadius: '6px',
                                                    border: '1px solid #ddd',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{file}</span>
                                                <Button
                                                    variant="link"
                                                    style={{ padding: 0, color: '#890411', textDecoration: 'none', fontSize: '16px', flexShrink: 0 }}
                                                    onClick={() => deleteUploadedFile(index)}
                                                >
                                                    ✕
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>)}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="text-start w-100">First Message</Form.Label>
                            <Form.Control name="firstMessage" type="text" placeholder="Enter first message" value={localConfig.firstMessage || ''} onChange={handleChange} required />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="selectAvatar">
                            <Form.Label className='text-start w-100'>Select Voice</Form.Label>
                            <Form.Select size="" name='voice' value={localConfig?.voice?.id || ''} onChange={handleSelectChange}>
                                {Voices.map((voice) =>
                                    (<option value={voice.id}>{voice.name}</option>)
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col lg={6} className='p-1 px-md-3 mt-0 py-lg-5'>
                        <Form.Group className="mb-4" controlId="selectAvatar">
                            <Form.Label className='text-start w-100'>Select Avatar</Form.Label>
                            <Form.Select size="" name='avatar' value={localConfig?.avatar?.id || ''} onChange={handleSelectChange}>
                                {Avatars.map((avatar) => <option value={avatar.id}>{avatar.name}</option>)}
                            </Form.Select>
                        </Form.Group>


                        {/* Image Preview */}
                        <div className='img-preview'>
                            <Image src={localConfig?.avatar?.preview} alt="Avatar-Preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "12px" }} />
                        </div>

                        <div className="d-flex justify-content-between gap-3 mt-md-4">
                            <div className='d-flex gap-2'>
                                <Button variant='success' className="px-4" onClick={goToDemo} disabled={!isChangeSave}>
                                    Test
                                </Button>
                                {localConfig.name === 'neo-allteam-assistant' && localConfig.agentName === 'neo-allteam-assistant' &&
                                    <Button variant='primary' onClick={deployAgent}>{loading === 'deploy-agent' ? (<><Spinner animation="border" size="sm" role="status" className="me-2" /> Deploying... </>) : 'Deploy'}</Button>}
                                <Button variant="secondary" onClick={updateConfig}>{loading === 'update-config' ? (<><Spinner animation="border" size="sm" role="status" className="me-2" /> Saving... </>) : 'Save Changes'}</Button>
                                <Button className="btn-custom" type='submit'>Save as</Button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Form>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Save Configuration</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <label className='mb-2'>Enter Configuration Name:</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Enter config name"
                        value={configName}
                        onChange={(e) => setConfigName(e.target.value)} />
                    {message && (
                        <p className='px-2' style={{ color: "#cf0606ff", fontSize: "12px" }}>{message}</p>
                    )
                    }
                    <div className='my-2 d-flex gap-2 justify-content-end'>
                        <Button variant='secondary' onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button className='btn-custom' type='submit' onClick={handleConfigSave}>{loading === 'save-config' ? (<><Spinner animation="border" size="sm" role="status" className="me-2" /> Indexing... </>) : 'Save'}</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    </>)
}

export default Configure;
